/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import { Container, Row, Col } from 'reactstrap';
import * as default_data from '../config.js';
import { getToken } from '../AuthService.js';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, FormGroup, FormLabel, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper } from '@mui/material';
import { FiEdit } from 'react-icons/fi';
import { getBannerColor } from '../Utils/classification_helper.js';

import AdminHorizontalMenu from '../Admin/AdminHorizontalMenu.js';

function SuperAdminOrganizationPurchasesList() {
  const { organization_id } = useParams();
  const [purchases, setPurchases] = useState([]);
  const [errorMessageAdd, setErrorMessageAdd] = useState('');
  const [errorMessageUpdate, setErrorMessageUpdate] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [purchase, setPurchase] = useState({
    date_purchase: '',
    email_payer: '',
    payer_comment: '',
    total_tokens: '',
    total_training_tokens: '',
    expiration: '',
    organization_id: organization_id
  });

  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Getting things ready...");    
  const loading_styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.9)', // Adjust the opacity as needed
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999, // Adjust the z-index as needed to ensure it appears above other elements
    },
    loadingText: {
      color: '#fff',
      fontSize: '24px',
    },
  };

  const [purchaseId, setPurchaseId] = useState(null);

  var loaded_static = false;

  function loadPurchases() {
    setIsLoading(true);
    var token = getToken();
    if (token === false) {
      return;
    }

    fetch(`${default_data.default_user_service_url_no_cac}/superadmin-get-organization-purchases/${organization_id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({})
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while getting data.');
      })
      .then(data => {
        if (data.status !== 200) {
          window.location = '/login';
          return;
        }
        data = data.response;
        setPurchases(data);
        setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        setIsLoading(false);
      });
  }

  useEffect(() => {
    if (loaded || loaded_static) {
      return;
    }

    setLoaded(true);
    loaded_static = true;

    loadPurchases();
  }, []);

  const handleChange = (event) => {
    setPurchase({ ...purchase, [event.target.name]: event.target.value });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const updatedPurchase = {
      ...purchase,
      date_purchase: convertLocalToGMT(purchase.date_purchase),
      expiration: convertLocalToGMT(purchase.expiration)
    };
    if (openAddDialog) {
      addPurchase(updatedPurchase);
    } else if (openUpdateDialog) {
      updatePurchase(updatedPurchase);
    }
  };

  function addPurchase(purchase) {
    setErrorMessageAdd('');
    var token = getToken();
    if (token === false) {
      return;
    }

    fetch(`${default_data.default_user_service_url_no_cac}/superadmin-add-organization-purchase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify(purchase)
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while adding the purchase.');
      })
      .then(data => {
        if (data.status !== 200) {
          throw new Error(data.response);
        }
        loadPurchases();
        setOpenAddDialog(false);
      })
      .catch(error => {
        setErrorMessageAdd(error.toString());
      });
  }

  function updatePurchase(purchase) {
    setErrorMessageUpdate('');
    if (typeof purchaseId !== 'number') {
      console.log('Invalid purchase_id');
      return;
    }

    var token = getToken();
    if (token === false) {
      return;
    }

    fetch(`${default_data.default_user_service_url_no_cac}/superadmin-update-organization-purchase/${purchaseId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify(purchase)
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('An error occurred while updating the purchase.');
      })
      .then(data => {
        if (data.status !== 200) {
          window.location = '/login';
          return;
        }
        loadPurchases();
        setOpenUpdateDialog(false);
      })
      .catch(error => {
        setErrorMessageUpdate(error.toString());
      });
  }

  const openUpdateDialogWithPurchase = (purchase) => {
    setPurchase({
      ...purchase,
      date_purchase: formatDateToGMT(purchase.date_purchase),
      expiration: formatDateToGMT(purchase.expiration)
    });
    setPurchaseId(purchase.id);
    setOpenUpdateDialog(true);
  };

  const DisplayData = purchases.map(
    (info) => {
      return (
        <TableRow key={info.id}>
          <TableCell className="compact-cell">{info.id}</TableCell>
          <TableCell className="compact-cell">{info.date_purchase}</TableCell>
          <TableCell className="compact-cell">{info.email_payer}</TableCell>
          <TableCell className="compact-cell">{info.payer_comment}</TableCell>
          <TableCell className="compact-cell">{info.total_tokens}</TableCell>
          <TableCell className="compact-cell">{info.total_training_tokens}</TableCell>
          <TableCell className="compact-cell">{info.expiration}</TableCell>
          <TableCell className="compact-cell"><IconButton variant="contained" onClick={() => openUpdateDialogWithPurchase(info)}><FiEdit /></IconButton></TableCell>
        </TableRow>
      );
    }
  );

  return (
    <div>
      <div className={`header-message ${getBannerColor(true)}`}>
        {default_data.default_banner_override != null && default_data.default_banner_override !== '' ?
          default_data.default_banner_override : 'CUI'
        }
      </div>
      <div className="reg-page-mode">
        <Container style={{ maxWidth: '100%' }}>
          <Row className="justify-content-center reg-page-mode-header">
            <Col xs="12" md="12" lg="12" className="d-flex align-items-center">
              <a href="/" className="d-flex align-items-center">
                <img src="/logo.png" alt="Ask Sage" className="logo" />
              </a>
              <h1 className="user-list-h1">
                Organization Purchases
              </h1>
            </Col>
          </Row>
          <Row className="user-list-reg-page-mode-content justify-content-center reg-page-mode-content" style={{ padding: '10px' }}>
            <Col xs="12" md="12" lg="12">
              <AdminHorizontalMenu type={'superadmin'} full_stats={0} />
              <br /><br />
              <Button variant="contained" onClick={() => setOpenAddDialog(true)}>Add new Purchase</Button>
              <div className="user-list-table-parent">
                <TableContainer component={Paper}>
                  <Table className="user-list-table">
                    <TableHead>
                      <TableRow>
                        <TableCell className="compact-cell">ID</TableCell>
                        <TableCell className="compact-cell">Date Purchase</TableCell>
                        <TableCell className="compact-cell">Email Payer</TableCell>
                        <TableCell className="compact-cell">Payer Comment</TableCell>
                        <TableCell className="compact-cell">Total Tokens</TableCell>
                        <TableCell className="compact-cell">Total Training Tokens</TableCell>
                        <TableCell className="compact-cell">Expiration</TableCell>
                        <TableCell className="compact-cell">Update</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {DisplayData.length > 0 ? DisplayData : <TableRow><TableCell colSpan={8}>Loading...</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Col>
          </Row>
          <Row className="justify-content-center reg-page-mode-footer">
            <Col xs="12" md="12" lg="12">
              Copyright &copy; {default_data.default_copywrite_year} Ask Sage, Inc.
            </Col>
          </Row>
        </Container>
      </div>

      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">Add New Purchase</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>Date Purchase*</FormLabel>
              <TextField type="datetime-local" name="date_purchase" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Email Payer*</FormLabel>
              <TextField type="email" name="email_payer" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Payer Comment</FormLabel>
              <TextField type="text" name="payer_comment" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Total Tokens*</FormLabel>
              <TextField type="number" name="total_tokens" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Total Training Tokens*</FormLabel>
              <TextField type="number" name="total_training_tokens" onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Expiration*</FormLabel>
              <TextField type="datetime-local" name="expiration" onChange={handleChange} fullWidth />
            </FormGroup>
            {errorMessageAdd && <p style={{ color: 'red' }}>{errorMessageAdd}</p>}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} color="primary">
            Submit
          </Button>
          <Button onClick={() => setOpenAddDialog(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openUpdateDialog}
        onClose={() => setOpenUpdateDialog(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">Update Purchase</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>Date Purchase*</FormLabel>
              <TextField type="datetime-local" name="date_purchase" value={purchase.date_purchase} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Email Payer*</FormLabel>
              <TextField type="email" name="email_payer" value={purchase.email_payer} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Payer Comment</FormLabel>
              <TextField type="text" name="payer_comment" value={purchase.payer_comment} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Total Tokens*</FormLabel>
              <TextField type="number" name="total_tokens" value={purchase.total_tokens} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Total Training Tokens*</FormLabel>
              <TextField type="number" name="total_training_tokens" value={purchase.total_training_tokens} onChange={handleChange} fullWidth />
            </FormGroup>
            <FormGroup>
              <FormLabel>Expiration*</FormLabel>
              <TextField type="datetime-local" name="expiration" value={purchase.expiration} onChange={handleChange} fullWidth />
            </FormGroup>
            {errorMessageUpdate && <p style={{ color: 'red' }}>{errorMessageUpdate}</p>}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} color="primary">
            Submit
          </Button>
          <Button onClick={() => setOpenUpdateDialog(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    {/* Loading overlay */}
    {isLoading && (
      <div style={loading_styles.overlay}>
        <span style={loading_styles.loadingText}>{loadingText}</span>
      </div>
    )}
    </div>
  );
}

export default SuperAdminOrganizationPurchasesList;

function formatDateToGMT(dateString) {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function convertLocalToGMT(dateString) {
  const date = new Date(dateString);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000).toISOString();
}